import TableHeader from "components/common/Table/TableHeader";
import React, { useEffect, useMemo, useState } from "react";
import { IoCheckmark, IoClose } from "react-icons/io5";
import TimesheetStatusPill from "../../components/TimesheetRevamp/TimesheetStatusPill";
import TimesheetTableCheckbox from "../../components/TimesheetRevamp/TimesheetTableCheckbox";

import {
  applyAvatarImageFallback,
  baseURLImage,
  capitalizeAllLetters,
  convertTimeToHrsMins,
  getNameProfile,
} from "Utils/utils";

import { Tooltip } from "react-tooltip";
import AvatarGroup from "components/common/Avatar/AvatarGroup";
import moment from "moment";

import Loader from "components/common/Loader/Loader";
import "./tooltip.css";
import { isBoolean } from "lodash";
import useCareLabels from "hooks/useCareLabels";
import { TimesheetAiStatusIcon, resolveAiStatusForTimesheet } from "../ai";
import {
  buildTimesheetConfirmDetails,
  hasScheduleActualMismatch,
  resolveShiftDateForConfirm,
} from "../../utils/timesheetConfirmDetails";
import {
  getSelectableStatusForFilter,
  isRowSelectableForFilter,
} from "../../utils/timesheetBulkUnapprove";
import {
  getAdvanceSlot,
  isAdvanceTimesheetRow,
} from "../../utils/advanceShiftSlots";

const getWorkerDisplayName = (worker, { inactiveSuffix = false } = {}) => {
  const name = [worker?.first_name, worker?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (!name) return "";
  const isInactive =
    (isBoolean(worker?.status) && worker?.status === false) ||
    String(worker?.status).toLowerCase() === "inactive";
  if (inactiveSuffix && isInactive) {
    return capitalizeAllLetters(`${name} (Inactive)`);
  }
  return capitalizeAllLetters(name);
};

const ResizeableTable = ({
  timesheetDetailsList,
  tableHeight,
  fillHeight = false,
  setCheckboxes,
  checkboxes = [],
  viewedTimesheetUuid,
  onRowSelect,
  handleStatusChange,
  requestApproveConfirmation,
  loadingStatus,
  participantLabelPlural: participantLabelPluralProp,
  aiStatusByTimesheetId = {},
  showAiColumn = false,
  onOpenAiReport,
  statusFilter = "all",
}) => {
  const { participantLabelPlural: participantLabelPluralHook } = useCareLabels();
  const participantLabelPlural =
    participantLabelPluralProp ?? participantLabelPluralHook ?? "Participants";
  const [allSelected, setAllSelected] = useState(false);
  // const [checkboxes, setCheckboxes] = useState([]);

  const handleImageError = (e, fname, lname) => {
    applyAvatarImageFallback(e, fname, lname);
  };

  const selectableStatus = useMemo(
    () => getSelectableStatusForFilter(statusFilter),
    [statusFilter]
  );

  const selectableRows = useMemo(
    () =>
      timesheetDetailsList?.data?.filter((item) =>
        isRowSelectableForFilter(item, statusFilter)
      ) || [],
    [timesheetDetailsList?.data, statusFilter]
  );

  const isRowSelectable = (item) => isRowSelectableForFilter(item, statusFilter);

  const syncSelectionState = (newCheckboxes) => {
    setCheckboxes(newCheckboxes);
    setAllSelected(
      selectableRows.length > 0 &&
        newCheckboxes.length === selectableRows.length
    );
  };

  const handleCheckboxChange = (checked, item) => {
    if (!isRowSelectable(item)) return;

    let newCheckboxes;
    if (checked) {
      newCheckboxes = checkboxes.some((c) => c.uuid === item.uuid)
        ? checkboxes
        : [...checkboxes, item];
    } else {
      newCheckboxes = checkboxes.filter((c) => c.uuid !== item.uuid);
    }

    syncSelectionState(newCheckboxes);
  };

  const handleRowClick = (item, event) => {
    event?.stopPropagation();
    onRowSelect?.(item);
  };
  // const toggleCheckboxes = (checked) => {
  //   console.log({ checked });
  //   if (checked) {
  //     setCheckboxes(timesheetDetailsList?.data?.slice() || []);
  //     setAllSelected(true);
  //   } else {
  //     setCheckboxes([]);
  //     setAllSelected(false);
  //   }
  // };

  const toggleCheckboxes = (checked) => {
    if (checked) {
      syncSelectionState(selectableRows);
    } else {
      syncSelectionState([]);
    }
  };

  useEffect(() => {
    if (selectableRows.length === 0) {
      setAllSelected(false);
      return;
    }
    setAllSelected(
      checkboxes.length > 0 && checkboxes.length === selectableRows.length
    );
  }, [checkboxes, selectableRows]);
  const isBulkMode = checkboxes.length > 1;

  const getToolTipContent = () => {
    return <div>The start time does not align with scheduled time</div>;
  };

  const headerClass =
    "sticky top-0 z-10 bg-[#F8FAFC] px-2.5 py-2 text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]";

  const formatShiftDate = (row) => {
    const m = resolveShiftDateForConfirm(row);
    if (!m) return { day: "—", date: "—" };
    return {
      day: m.format("dddd"),
      date: m.format("DD/MM/YY"),
    };
  };

  const formatTimeStack = (start, end) => ({
    start: start
      ? moment(start, "YYYY-MM-DD HH:mm:ss").format("h:mm A")
      : "—",
    end: end
      ? moment(end, "YYYY-MM-DD HH:mm:ss").format("h:mm A")
      : "—",
  });

  const isRowDisabled = (item) =>
    !isRowSelectable(item) &&
    (item?.status === "approved" ||
      item?.status === "rejected" ||
      item?.status === "Expired");

  const scrollClass = fillHeight
    ? "h-full min-h-[80px] overflow-x-auto overflow-y-auto"
    : "";

  return (
    <div className={`min-w-full ${fillHeight ? "h-full" : ""}`}>
      <div
        className={scrollClass}
        data-timesheet-scroll
        style={
          fillHeight
            ? undefined
            : {
                overflowX: "auto",
                height: `${tableHeight}px`,
                overflowY: "auto",
              }
        }
      >
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-[#E5E7EB]">
              <th className={`${headerClass} w-10 text-center`}>
                <div className="flex justify-center">
                  <TimesheetTableCheckbox
                    checked={allSelected}
                    disabled={!selectableStatus || selectableRows.length === 0}
                    onChange={(checked) => toggleCheckboxes(checked)}
                  />
                </div>
              </th>
              <TableHeader isSort={false} label="Worker" cssClass={headerClass} />
              <TableHeader
                isSort={false}
                label={participantLabelPlural}
                cssClass={headerClass}
              />
              <TableHeader
                isSort={false}
                label="Shift Date"
                cssClass={headerClass}
              />
              <TableHeader isSort={false} label="Schedule" cssClass={headerClass} />
              <TableHeader isSort={false} label="Actual" cssClass={headerClass} />
              <TableHeader isSort={false} label="Status" cssClass={headerClass} />
              {showAiColumn ? (
                <TableHeader
                  isSort={false}
                  label="AI"
                  cssClass={`${headerClass} min-w-[4.5rem] text-left`}
                />
              ) : null}
              <TableHeader isSort={false} label="Total" cssClass={headerClass} />
              <TableHeader
                isSort={false}
                label="Action"
                cssClass={`${headerClass} text-center`}
              />
            </tr>
          </thead>
          <tbody className="bg-white">
            {timesheetDetailsList?.data?.length > 0 ? (
              timesheetDetailsList?.data?.map((item, index) => {
                const isLoading = loadingStatus[item.uuid] || false;
                const isBulkSelected = checkboxes.some(
                  (x) => x.uuid === item.uuid
                );
                const isViewed =
                  !isBulkMode && viewedTimesheetUuid === item.uuid;
                const schedule = formatTimeStack(item?.start_time, item?.end_time);
                const actual = formatTimeStack(
                  item?.worker_start_time,
                  item?.worker_end_time
                );
                const shiftDate = formatShiftDate(item);
                const rowMuted = isRowDisabled(item);
                const isAdvanceRow = isAdvanceTimesheetRow(item);
                const advanceSlot = getAdvanceSlot(item);
                // Advance shifts are rostered per worker, so the row shows that
                // worker's own slot rather than the whole shift window.
                const scheduleStack = advanceSlot
                  ? formatTimeStack(
                      advanceSlot.scheduled_start_time ?? item?.start_time,
                      advanceSlot.scheduled_end_time ?? item?.end_time
                    )
                  : schedule;
                const actualStack = advanceSlot
                  ? formatTimeStack(
                      advanceSlot.actual_start_time ?? item?.worker_start_time,
                      advanceSlot.actual_end_time ?? item?.worker_end_time
                    )
                  : actual;

                return (
                  <tr
                    key={index}
                    className={`cursor-pointer border-b border-[#E5E7EB] text-xs transition-colors ${
                      isViewed
                        ? "bg-[#EEF2FB] ring-1 ring-inset ring-[#C5D4EA]"
                        : isBulkSelected
                        ? "bg-[#F1F5F9]"
                        : "hover:bg-[#F8FAFC]"
                    } ${rowMuted ? "opacity-75" : ""}`}
                    onClick={(e) => handleRowClick(item, e)}
                  >
                    <td className="whitespace-nowrap px-2.5 py-3">
                      <TimesheetTableCheckbox
                        checked={Boolean(
                          checkboxes.find((x) => x.uuid === item.uuid)
                        )}
                        disabled={!isRowSelectable(item)}
                        onChange={(checked) => handleCheckboxChange(checked, item)}
                      />
                    </td>
                    <td
                      className="px-2.5 py-3 text-xs capitalize text-[#0F172A]"
                      title={getWorkerDisplayName(item?.worker, {
                        inactiveSuffix: true,
                      })}
                    >
                      <div
                        className={`flex items-center gap-2 min-w-0 ${
                          (isBoolean(item?.worker?.status) &&
                            item?.worker?.status === false) ||
                          String(item?.worker?.status).toLowerCase() ===
                            "inactive"
                            ? "opacity-50 text-red-500"
                            : ""
                        }`}
                      >
                        <div className="h-8 w-8 flex-shrink-0">
                          <img
                            onError={(e) =>
                              handleImageError(
                                e,
                                item?.worker?.first_name || "",
                                item?.worker?.last_name || ""
                              )
                            }
                            className="object-cover h-8 w-8 rounded-full"
                            src={
                              item?.worker?.photo
                                ? baseURLImage(item?.worker?.photo)
                                : getNameProfile(
                                    item?.worker?.first_name || "",
                                    item?.worker?.last_name || ""
                                  )
                            }
                            alt=""
                          />
                        </div>
                        <span className="min-w-0 flex-1 text-xs font-semibold">
                          {getWorkerDisplayName(item?.worker)}
                        </span>
                      </div>
                    </td>
                    <td className="px-2.5 py-3 text-xs capitalize text-[#0F172A]">
                      {item?.participants?.length > 0 &&
                      item?.participants?.length === 1 ? (
                        <div
                          className={`flex items-center gap-1 min-w-0 ${item?.participants[0]?.status !== "active" ? "opacity-50 text-red-500" : ""}`}
                          title= {
                            item?.participants[0]?.status !== "active" ? capitalizeAllLetters(
                            `${item?.participants[0]?.first_name} ${item?.participants[0]?.last_name} (Inactive)` 
                          ) : capitalizeAllLetters(
                            `${item?.participants[0]?.first_name} ${item?.participants[0]?.last_name}`
                          )
                          }
                        >
                          <div className='h-9 w-9 flex-shrink-0'>
                            <img
                              onError={(e) =>
                                handleImageError(
                                  e,
                                  `${item?.participants[0]?.first_name}`,
                                  `${item?.participants[0]?.last_name}`
                                )
                              }
                              className='object-cover h-9 w-9 rounded-full'
                              src={
                                item?.participants[0]?.profile_picture
                                  ? baseURLImage(
                                      item?.participants[0]?.profile_picture
                                    )
                                  : getNameProfile(
                                      item?.participants[0]?.first_name,
                                      item?.participants[0]?.last_name
                                    )
                              }
                              alt=''
                            />
                          </div>
                          <span className='break-words overflow-wrap-anywhere min-w-0 flex-1'>
                            {capitalizeAllLetters(
                              `${item?.participants[0]?.first_name} ${item?.participants[0]?.last_name}`
                            )}
                          </span>
                        </div>
                      ) : (
                        <AvatarGroup
                          type='participant'
                          arrayData={item?.participants || []}
                          cssClass='mt-0'
                          maxLength={3}
                          isNoRing={true}
                        />
                      )}
                    </td>
                    <td className="px-2.5 py-3 whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-[#0F172A]">
                          {shiftDate.day}
                        </span>
                        <span className="text-xs font-medium text-[#64748B]">
                          {shiftDate.date}
                        </span>
                      </div>
                    </td>
                    <td className="px-2.5 py-3 whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-[#0F172A]">
                          {scheduleStack.start}
                        </span>
                        <span className="text-xs font-medium text-[#64748B]">
                          {scheduleStack.end}
                        </span>
                      </div>
                    </td>
                    <td className="px-2.5 py-3 whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        {!item?.time_matched ? (
                          <a data-tooltip-id={`timsheet_time_missmatch_${item.uuid}`}>
                            <span className="text-xs font-semibold text-[#FC466C]">
                              {actualStack.start}
                            </span>
                          </a>
                        ) : (
                          <span className="text-xs font-semibold text-[#0F172A]">
                            {actualStack.start}
                          </span>
                        )}
                        <span className="text-xs font-medium text-[#64748B]">
                          {actualStack.end}
                        </span>
                        {!item?.time_matched && (
                          <Tooltip
                            id={`timsheet_time_missmatch_${item.uuid}`}
                            place="top"
                            effect="solid"
                            content={getToolTipContent()}
                            delayShow={300}
                            className="border border-[#FE426D]"
                            style={{
                              backgroundColor: "#FFD1DC",
                              color: "#000",
                              border: "1px solid #FE426D",
                              zIndex: 9999,
                            }}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-2.5 py-3 whitespace-nowrap">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <TimesheetStatusPill status={item?.status} />
                        {isAdvanceRow ? (
                          <span
                            className="inline-flex items-center rounded-full bg-[#EEF2FB] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#4A6BB6]"
                            title="Advance shift — this worker has their own slot"
                          >
                            Advance
                          </span>
                        ) : null}
                        {item?.created_from_mobile ? (
                          <span
                            className="inline-flex items-center rounded-full bg-[#EEF2FB] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#4A6BB6]"
                            title="Created from mobile"
                          >
                            Mobile
                          </span>
                        ) : null}
                      </div>
                    </td>
                    {showAiColumn ? (
                      <td
                        className="whitespace-nowrap px-2 py-3 text-left"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TimesheetAiStatusIcon
                          timesheetId={Number(item?.id)}
                          status={resolveAiStatusForTimesheet(
                            item?.id,
                            item?.ai_anomaly,
                            aiStatusByTimesheetId
                          )}
                          onOpenReport={onOpenAiReport}
                        />
                      </td>
                    ) : null}
                    <td className="px-2.5 py-3 whitespace-nowrap text-xs font-semibold text-[#0F172A]">
                      {convertTimeToHrsMins(item?.timesheet_length)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        {isLoading ? (
                          <Loader />
                        ) : (
                          <>
                            <button
                              type="button"
                              title="Approve timesheet"
                              disabled={item?.status !== "pending"}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (item?.status !== "pending") return;

                                const confirmed =
                                  await requestApproveConfirmation?.({
                                    scheduleMismatch:
                                      hasScheduleActualMismatch(item),
                                    details:
                                      buildTimesheetConfirmDetails(item),
                                  });

                                if (confirmed) {
                                  handleStatusChange(item, "approved");
                                }
                              }}
                              className={`flex h-8 w-8 items-center justify-center rounded-full bg-[#E2F8F6] ${
                                item?.status !== "pending"
                                  ? "cursor-not-allowed opacity-30"
                                  : "hover:bg-[#0DBBB3]/20"
                              }`}
                            >
                              <IoCheckmark className="h-4 w-4 text-[#0DBBB3]" />
                            </button>
                            <button
                              type="button"
                              title="Reject timesheet"
                              disabled={item?.status !== "pending"}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (item?.status !== "pending") return;
                                handleStatusChange(item, "rejected");
                              }}
                              className={`flex h-8 w-8 items-center justify-center rounded-full bg-[#FEE9EE] ${
                                item?.status !== "pending"
                                  ? "cursor-not-allowed opacity-30"
                                  : "hover:bg-[#FC466C]/20"
                              }`}
                            >
                              <IoClose className="h-4 w-4 text-[#FC466C]" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={showAiColumn ? 10 : 9}
                  className="text-center py-8 text-sm text-[#94A3B8]"
                >
                  No timesheet data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResizeableTable;
